import { useState, useEffect, useCallback, RefObject, useRef } from 'react'

export const useMultipleItemsDrag = (
  elements: (HTMLDivElement | null)[],
  onDragEnd: (e: MouseEvent) => void,
  onDragMove: (deltaX: number, e: MouseEvent) => void,
  onDragStart: () => void,
  isPreventDrag: boolean,
  scrollableContainerRef: RefObject<HTMLElement | null>
) => {
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [hasDragged, setHasDragged] = useState(false)
  const initialScrollLeft = useRef(0)

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (isPreventDrag) {
        return
      }

      onDragStart()
      setIsDragging(true)
      setStartX(e.clientX)

      // Capture initial scroll position
      if (scrollableContainerRef?.current) {
        initialScrollLeft.current = scrollableContainerRef.current.scrollLeft
      }
    },
    // TODO: @gev-davtyan-rf remove this comment if onDragStart was not passed as a dependency for a reason
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setIsDragging, isPreventDrag, scrollableContainerRef]
  )

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        onDragEnd(e)
        setStartX(0)
      }

      setIsDragging(false)

      setTimeout(() => {
        setHasDragged(false)
      })
    },
    [isDragging, onDragEnd]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        let adjustedClientX = e.clientX

        // Adjust for scroll changes since drag started
        if (scrollableContainerRef?.current) {
          const currentScrollLeft = scrollableContainerRef.current.scrollLeft
          const scrollDelta = currentScrollLeft - initialScrollLeft.current
          adjustedClientX = e.clientX + scrollDelta
        }

        const deltaX = adjustedClientX - startX

        // const deltaX = e.clientX - startX
        onDragMove(deltaX, e)
        setHasDragged(true)
      }
    },
    [isDragging, startX, onDragMove, scrollableContainerRef]
  )

  useEffect(() => {
    elements.forEach(element => {
      element?.addEventListener('mousedown', handleMouseDown)
    })

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      elements.forEach(element => {
        element?.removeEventListener('mousedown', handleMouseDown)
      })

      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [elements, handleMouseDown, handleMouseMove, handleMouseUp])

  return { hasDragged, isDragging }
}
